// frontend/src/context/WesScopeContext.js
//
// Shared "scope" state for the wes-ai surface: which environment the user is
// currently working inside and which project (if any). The Phase 3.3 scope
// bar in the AppShell writes to this context; Phase 3.4 AIAgentsPage reads
// from it to filter agents and to decide where a newly created agent lands.
//
// Persistence: selected env + project ids are kept in localStorage so the
// choice survives a refresh. Lists themselves are re-fetched each mount —
// they can go stale quickly when the user or an admin adds projects.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import aiAgentService from '../services/aiAgentService';

const WesScopeContext = createContext(null);

const LS_ENV_KEY = 'hub360:wes-scope:env-id';
const LS_PROJECT_KEY = 'hub360:wes-scope:project-id';

function readStored(key) {
  try { return localStorage.getItem(key) || null; } catch { return null; }
}
function writeStored(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {}
}

export function WesScopeProvider({ children }) {
  const { authenticated } = useAuth();

  const [environments, setEnvironments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedEnvId, setSelectedEnvIdRaw] = useState(() => readStored(LS_ENV_KEY));
  const [selectedProjectId, setSelectedProjectIdRaw] = useState(() => readStored(LS_PROJECT_KEY));

  const setSelectedEnvId = useCallback((id) => {
    setSelectedEnvIdRaw(id);
    writeStored(LS_ENV_KEY, id);
    // Changing env invalidates the project selection — it may not belong
    // to the new env.
    setSelectedProjectIdRaw(null);
    writeStored(LS_PROJECT_KEY, null);
  }, []);

  const setSelectedProjectId = useCallback((id) => {
    setSelectedProjectIdRaw(id);
    writeStored(LS_PROJECT_KEY, id);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [envs, projs] = await Promise.all([
        aiAgentService.listEnvironments(),
        aiAgentService.listProjects(),
      ]);
      setEnvironments(Array.isArray(envs) ? envs : []);
      setProjects(Array.isArray(projs) ? projs : []);

      // If nothing was selected yet (or the stored id is no longer valid),
      // default to the first env/project so the scope bar never shows a
      // broken placeholder.
      if (Array.isArray(envs) && envs.length) {
        const currentStillValid = envs.some((e) => e.id === selectedEnvId);
        if (!currentStillValid) {
          setSelectedEnvIdRaw(envs[0].id);
          writeStored(LS_ENV_KEY, envs[0].id);
          setSelectedProjectIdRaw(null);
          writeStored(LS_PROJECT_KEY, null);
        }
      }
    } catch (err) {
      // 401/403 during startup are expected before the user signs in; only
      // surface real errors. Keep silent to avoid banner noise on the
      // dashboard shell.
      const status = err?.response?.status;
      if (status && status !== 401 && status !== 403) {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    refresh();
  }, [authenticated, refresh]);

  const currentEnvironment = useMemo(
    () => environments.find((e) => e.id === selectedEnvId) || environments[0] || null,
    [environments, selectedEnvId]
  );

  const projectsInCurrentEnv = useMemo(() => {
    if (!currentEnvironment) return projects;
    return projects.filter((p) => (
      p.environment_id === currentEnvironment.id
      || p.environment_slug === currentEnvironment.slug
    ));
  }, [projects, currentEnvironment]);

  const currentProject = useMemo(
    () => projectsInCurrentEnv.find((p) => p.project_id === selectedProjectId) || null,
    [projectsInCurrentEnv, selectedProjectId]
  );

  const value = useMemo(() => ({
    environments,
    projects,
    projectsInCurrentEnv,
    currentEnvironment,
    currentProject,
    selectedEnvId: currentEnvironment?.id || null,
    selectedProjectId: currentProject?.project_id || null,
    loading,
    error,
    refresh,
    setSelectedEnvId,
    setSelectedProjectId,
  }), [
    environments, projects, projectsInCurrentEnv, currentEnvironment, currentProject,
    loading, error, refresh, setSelectedEnvId, setSelectedProjectId,
  ]);

  return <WesScopeContext.Provider value={value}>{children}</WesScopeContext.Provider>;
}

export function useWesScope() {
  const ctx = useContext(WesScopeContext);
  if (!ctx) {
    throw new Error('useWesScope must be used within a WesScopeProvider');
  }
  return ctx;
}
